/**
 * User-Agent detection utility for browser and OS detection.
 * Uses User-Agent Client Hints API (modern) with fallback to userAgent string parsing.
 */

interface BrowserInfo {
  name: string
  version: string
}

interface OSInfo {
  name: string
  version?: string
}

interface UserAgentData {
  browser?: BrowserInfo
  os?: OSInfo
}

/**
 * Detect browser and OS using User-Agent Client Hints API (modern browsers)
 */
async function detectWithClientHints(): Promise<UserAgentData | null> {
  // Check if User-Agent Client Hints API is available
  if (!('userAgentData' in navigator)) {
    return null
  }

  try {
    const uaData = (navigator as any).userAgentData
    
    // Request high-entropy values (requires user permission in some browsers)
    const hints = await uaData.getHighEntropyValues([
      'platform',
      'platformVersion',
      'uaFullVersion',
      'fullVersionList'
    ])

    const result: UserAgentData = {}

    // Detect browser
    if (hints.fullVersionList && hints.fullVersionList.length > 0) {
      // Get the first (main) browser from the version list
      const mainBrowser = hints.fullVersionList[0]
      result.browser = {
        name: formatBrowserName(mainBrowser.brand),
        version: hints.uaFullVersion || mainBrowser.version || ''
      }
    } else if (uaData.brands && uaData.brands.length > 0) {
      // Fallback to brands array
      const mainBrand = uaData.brands[0]
      result.browser = {
        name: formatBrowserName(mainBrand.brand),
        version: mainBrand.version || ''
      }
    }

    // Detect OS
    if (hints.platform) {
      result.os = {
        name: formatOSName(hints.platform),
        version: hints.platformVersion || undefined
      }
    }

    return result.browser || result.os ? result : null
  } catch (error) {
    // Client Hints API may require permission or fail silently
    console.debug('User-Agent Client Hints API failed:', error)
    return null
  }
}

/**
 * Detect browser and OS using userAgent string parsing (fallback)
 */
function detectWithUserAgent(): UserAgentData {
  const ua = navigator.userAgent
  const result: UserAgentData = {}

  // Detect browser
  const browserMatch = detectBrowserFromUA(ua)
  if (browserMatch) {
    result.browser = browserMatch
  }

  // Detect OS
  const osMatch = detectOSFromUA(ua)
  if (osMatch) {
    result.os = osMatch
  }

  return result
}

/**
 * Parse browser from userAgent string
 */
function detectBrowserFromUA(ua: string): BrowserInfo | null {
  // Chrome (including Edge on Chromium)
  if (/Edg\//.test(ua)) {
    const match = ua.match(/Edg\/(\d+\.\d+)/)
    return {
      name: 'Edge',
      version: match ? match[1] : ''
    }
  }
  
  // Chrome
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) {
    const match = ua.match(/Chrome\/(\d+\.\d+)/)
    return {
      name: 'Chrome',
      version: match ? match[1] : ''
    }
  }

  // Firefox
  if (/Firefox\//.test(ua)) {
    const match = ua.match(/Firefox\/(\d+\.\d+)/)
    return {
      name: 'Firefox',
      version: match ? match[1] : ''
    }
  }

  // Safari (must check before Chrome as Chrome UA contains Safari)
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
    const match = ua.match(/Version\/(\d+\.\d+)/)
    return {
      name: 'Safari',
      version: match ? match[1] : ''
    }
  }

  return null
}

/**
 * Parse OS from userAgent string
 */
function detectOSFromUA(ua: string): OSInfo | null {
  // Windows
  if (/Windows NT/.test(ua)) {
    const versionMatch = ua.match(/Windows NT (\d+\.\d+)/)
    if (versionMatch) {
      const version = parseFloat(versionMatch[1])
      const osName = 'Windows'
      let osVersion: string | undefined

      // Map Windows NT versions to friendly names
      if (version === 10.0) {
        osVersion = '11' // Windows 11 reports as NT 10.0
        // Try to detect Windows 11 vs 10 (best effort)
        if (/Windows NT 10\.0; Win64; x64/.test(ua)) {
          // Could be 10 or 11, default to 11 for newer systems
          osVersion = '11'
        }
      } else if (version === 6.3) {
        osVersion = '8.1'
      } else if (version === 6.2) {
        osVersion = '8'
      } else if (version === 6.1) {
        osVersion = '7'
      } else {
        osVersion = versionMatch[1]
      }

      return { name: osName, version: osVersion }
    }
    return { name: 'Windows' }
  }

  // macOS
  if (/Mac OS X/.test(ua)) {
    const match = ua.match(/Mac OS X (\d+)[._](\d+)/)
    if (match) {
      return {
        name: 'macOS',
        version: `${match[1]}.${match[2]}`
      }
    }
    return { name: 'macOS' }
  }

  // iOS
  if (/iPhone|iPad|iPod/.test(ua)) {
    const match = ua.match(/OS (\d+)[._](\d+)/)
    if (match) {
      return {
        name: 'iOS',
        version: `${match[1]}.${match[2]}`
      }
    }
    return { name: 'iOS' }
  }

  // Android
  if (/Android/.test(ua)) {
    const match = ua.match(/Android (\d+\.\d+)/)
    if (match) {
      return {
        name: 'Android',
        version: match[1]
      }
    }
    return { name: 'Android' }
  }

  // Linux
  if (/Linux/.test(ua) && !/Android/.test(ua)) {
    return { name: 'Linux' }
  }

  return null
}

/**
 * Format browser name from brand string
 */
function formatBrowserName(brand: string): string {
  const normalized = brand.toLowerCase()
  
  if (normalized.includes('chrome')) return 'Chrome'
  if (normalized.includes('edge')) return 'Edge'
  if (normalized.includes('firefox')) return 'Firefox'
  if (normalized.includes('safari')) return 'Safari'
  
  // Capitalize first letter
  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
}

/**
 * Format OS name from platform string
 */
function formatOSName(platform: string): string {
  const normalized = platform.toLowerCase()
  
  if (normalized.includes('win')) return 'Windows'
  if (normalized.includes('mac')) return 'macOS'
  if (normalized.includes('iphone') || normalized.includes('ipad')) return 'iOS'
  if (normalized.includes('android')) return 'Android'
  if (normalized.includes('linux')) return 'Linux'
  
  // Capitalize first letter
  return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase()
}

/**
 * Detect Safari synchronously (macOS/iOS). Excludes Chromium-based browsers.
 */
export function isSafariBrowser(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return (
    /Safari\//.test(ua) &&
    !/Chrome\//.test(ua) &&
    !/Edg\//.test(ua) &&
    !/Chromium\//.test(ua) &&
    !/CriOS\//.test(ua) &&
    !/EdgiOS\//.test(ua) &&
    !/FxiOS\//.test(ua)
  )
}

/**
 * Get formatted browser string (e.g., "Chrome 120.0.0.0")
 */
export async function getBrowserString(): Promise<string> {
  try {
    // Try Client Hints first
    const clientHints = await detectWithClientHints()
    if (clientHints?.browser) {
      const { name, version } = clientHints.browser
      return version ? `${name} ${version}` : name
    }

    // Fallback to userAgent parsing
    const uaResult = detectWithUserAgent()
    if (uaResult.browser) {
      const { name, version } = uaResult.browser
      return version ? `${name} ${version}` : name
    }
  } catch (error) {
    console.debug('Error detecting browser:', error)
  }

  return ''
}

/**
 * Get formatted OS string (e.g., "Windows 11", "macOS 14.2")
 */
export async function getOSString(): Promise<string> {
  try {
    // Try Client Hints first
    const clientHints = await detectWithClientHints()
    if (clientHints?.os) {
      const { name, version } = clientHints.os
      return version ? `${name} ${version}` : name
    }

    // Fallback to userAgent parsing
    const uaResult = detectWithUserAgent()
    if (uaResult.os) {
      const { name, version } = uaResult.os
      return version ? `${name} ${version}` : name
    }
  } catch (error) {
    console.debug('Error detecting OS:', error)
  }

  return ''
}

/**
 * Get both browser and OS info
 */
export async function getUserAgentInfo(): Promise<{ browser: string; os: string }> {
  const [browser, os] = await Promise.all([
    getBrowserString(),
    getOSString()
  ])

  return { browser, os }
}

